/**
 * Les mentions légales, la politique de confidentialité et le verdict cookies
 * d'un site internet.
 *
 * TROIS SORTIES POUR UN SEUL QUESTIONNAIRE. Les deux documents se nourrissent
 * des mêmes réponses (qui édite, où c'est hébergé, ce que le site collecte,
 * quels outils il charge) et se contrediraient au premier oubli s'ils étaient
 * remplis séparément : un hébergeur cité dans les mentions et absent des
 * destinataires de la politique, un outil de mesure d'audience déclaré d'un
 * côté et pas de l'autre. Ils partent donc d'ici, ensemble.
 *
 * LE VERDICT COOKIES N'EST PAS UN DOCUMENT. Un bandeau de consentement se
 * règle dans un outil ou dans le code du site, il ne se colle pas : on dit
 * s'il en faut un, ce qu'il doit bloquer et ce qu'il peut laisser passer.
 *
 * L'ÉDITEUR RÉPOND DU CONTENU, PAS CELUI QUI L'INTÈGRE. Quand le parcours est
 * rempli pour un client (`pourQui: "client"`), les documents restent écrits au
 * nom du client, et une fiche de validation s'y ajoute : le client y atteste
 * avoir fourni les informations et relu le texte. C'est la réponse à la
 * question que se pose tout freelance : où s'arrête son rôle.
 *
 * PLUSIEURS HÉBERGEURS. Un site moderne a souvent son interface chez l'un
 * (Vercel, Netlify) et son serveur ou sa base chez un autre (Render, Supabase,
 * OVHcloud). La loi demande d'identifier l'hébergeur : quand ils sont
 * plusieurs, chacun est nommé avec ce qu'il héberge.
 *
 * Les documents s'écrivent en français, quelle que soit la langue de
 * l'interface : ce sont des obligations du droit français, lues par des
 * visiteurs et des autorités françaises.
 */

import {
    adressePostale,
    dateEnToutesLettres,
    identite,
    lignesDAdresse,
    nettoyer,
} from "./documentText.js";
import { HEBERGEURS, OUTILS_TIERS, ROLES_HEBERGEMENT, TRAITEMENTS } from "./legalNoticeCatalog.js";

export { HEBERGEURS, OUTILS_TIERS, ROLES_HEBERGEMENT, TRAITEMENTS };

/** Au-delà, on ne parle plus d'un site de solopreneur. */
export const HEBERGEURS_MAXIMUM = 4;

/* Les sociétés d'exercice libéral sont la forme des professions réglementées. */
export const FORMES_SOCIALES = ["EURL", "SARL", "SASU", "SAS", "SA", "SNC", "SCI", "SELARL", "SELARLU", "SELAS", "SELASU"];

/** Ce que le site peut collecter, dans l'ordre où le parcours le demande. */
export const COLLECTES = ["contact", "newsletter", "compte", "commandes", "rendezVous", "discussion"];

const CHIFFRES = (valeur) => nettoyer(valeur).replace(/[\s.]/g, "");

/** Le SIREN, lu dans un SIREN ou un SIRET : les neuf premiers chiffres. */
export function siren(valeur) {
    const brut = CHIFFRES(valeur);
    if (/^\d{9}$/.test(brut) || /^\d{14}$/.test(brut)) return brut.slice(0, 9);
    return "";
}

/** « 123 456 789 », comme l'écrit l'avis de situation de l'INSEE. */
export function sirenLisible(valeur) {
    const s = siren(valeur);
    return s ? `${s.slice(0, 3)} ${s.slice(3, 6)} ${s.slice(6)}` : "";
}

/**
 * Le contrôle de Luhn, que portent le SIREN et le SIRET.
 *
 * Une faute de frappe sur un chiffre le fait échouer neuf fois sur dix. La
 * Poste fait exception (ses SIRET ne le respectent pas), mais un solopreneur
 * n'édite pas le site de La Poste.
 */
export function luhn(chiffres) {
    let somme = 0;
    for (let i = 0; i < chiffres.length; i++) {
        let n = Number(chiffres[chiffres.length - 1 - i]);
        if (i % 2 === 1) {
            n *= 2;
            if (n > 9) n -= 9;
        }
        somme += n;
    }
    return somme % 10 === 0;
}

/** Le numéro de TVA intracommunautaire français, clé comprise. */
export function tvaFrancaiseValide(numero) {
    const m = /^FR([0-9A-Z]{2})(\d{9})$/.exec(CHIFFRES(numero).toUpperCase());
    if (!m) return false;
    const [, cle, s] = m;
    if (!/^\d{2}$/.test(cle)) return true; // Clés alphanumériques : rares, non calculées.
    return Number(cle) === (12 + 3 * (Number(s) % 97)) % 97;
}

/** Une adresse électronique plausible : on ne cherche pas à tout refuser. */
export function emailValide(valeur) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(nettoyer(valeur));
}

/** Une adresse de site lisible, sans le protocole ni la barre finale. */
export function urlLisible(valeur) {
    return nettoyer(valeur)
        .replace(/^https?:\/\//i, "")
        .replace(/\/+$/, "");
}

/** Le nom de domaine se tape sans protocole ; on accepte les deux. */
export function urlValide(valeur) {
    return /^[a-z0-9-]+(\.[a-z0-9-]+)+(\/\S*)?$/i.test(urlLisible(valeur));
}

/* --------------------------------------------------------------------- */
/* Les hébergeurs                                                         */
/* --------------------------------------------------------------------- */

/**
 * Un hébergeur tel qu'il sera écrit : celui du catalogue, complété de ce que
 * le lecteur a saisi là où le catalogue n'a rien.
 *
 * Le catalogue ne connaît que ce que l'hébergeur publie lui-même. Quand il ne
 * publie pas de téléphone, le champ reste au lecteur : nous n'en inventons
 * pas, et nous ne recopions pas celui d'un annuaire.
 *
 * LE LECTEUR PEUT NE PAS L'OBTENIR. Un hébergeur sans service téléphonique
 * ne lui en donnera pas, et bloquer ses documents pour autant le laisserait
 * sans mentions du tout, ce qui est pire. Il le dit (`telephoneInconnu`), la
 * ligne l'écrit telle quelle, et le résultat le prévient que la loi demande ce
 * numéro.
 */
export function hebergeurComplet(choix) {
    const fiche = choix?.id && choix.id !== "autre" ? HEBERGEURS[choix.id] : null;
    const raisonSociale = fiche?.raisonSociale || nettoyer(choix?.raisonSociale);
    const adresse = fiche?.adresse || nettoyer(choix?.adresse);
    // La case « je n'ai pas pu l'obtenir » l'emporte sur un numéro tapé avant
    // elle : le champ a disparu de l'écran, il ne doit pas reparaître écrit.
    const telephoneInconnu = !fiche?.telephone && choix?.telephoneInconnu === true;
    const saisi = fiche?.telephone || (telephoneInconnu ? "" : nettoyer(choix?.telephone));
    const telephone = saisi || (telephoneInconnu ? "non communiqué par l'hébergeur" : "");
    return {
        telephoneInconnu,
        id: choix?.id || "",
        role: choix?.role || "site",
        raisonSociale,
        adresse,
        telephone,
        horsUe: fiche ? fiche.horsUe : choix?.horsUe === true,
        pays: fiche?.pays || nettoyer(choix?.pays),
        catalogue: Boolean(fiche),
        telephoneFourni: Boolean(fiche?.telephone),
    };
}

export function hebergeursComplets(r) {
    const liste = (r?.hebergeurs || []).filter((h) => h?.id).map(hebergeurComplet);
    // Seul, un hébergeur porte tout le site, quel que soit le rôle qu'il
    // gardait d'un choix à plusieurs défait depuis.
    if (liste.length === 1) liste[0].role = "site";
    return liste;
}

/**
 * Les outils qu'aucun catalogue ne connaît, décrits par le lecteur.
 *
 * Sans eux, la fiche de validation ferait attester au client « tous les
 * outils tiers chargés par le site » alors que le parcours ne lui laisse
 * déclarer que ceux de notre liste.
 */
export function autresOutils(r) {
    return (r?.autresOutils || [])
        .map((o) => ({
            nom: nettoyer(o?.nom),
            societe: nettoyer(o?.societe),
            pays: nettoyer(o?.pays),
            horsUe: o?.horsUe === true,
            traceurs: o?.traceurs === true,
            usage: nettoyer(o?.usage),
        }))
        .filter((o) => o.nom || o.societe || o.usage);
}

/* --------------------------------------------------------------------- */
/* Le verdict cookies                                                     */
/* --------------------------------------------------------------------- */

/**
 * Faut-il un bandeau, et pour quoi.
 *
 * Un outil peut dépendre de son réglage : une mesure d'audience exemptée ne
 * l'est qu'à des conditions précises. Le lecteur dit s'il les remplit
 * (`reglages[id] === "exempte"`) ; sans réponse, on le range parmi ce qui
 * demande le consentement, parce que c'est l'erreur la moins coûteuse.
 */
export function verdictCookies(r) {
    const choisis = Object.keys(r?.outils || {}).filter((id) => r.outils[id] && OUTILS_TIERS[id]);
    const consentement = [];
    const exemptes = [];
    const sansTraceur = [];
    for (const id of choisis) {
        const outil = OUTILS_TIERS[id];
        if (outil.cookies === "consentement") consentement.push(id);
        else if (outil.cookies === "exempte") exemptes.push(id);
        else if (outil.cookies === "aucun") sansTraceur.push(id);
        else if (outil.cookies === "reglage") {
            if (r?.reglages?.[id] === "exempte") exemptes.push(id);
            else consentement.push(id);
        }
    }
    const autres = autresOutils(r).filter((o) => o.traceurs).map((o) => o.nom || o.societe);
    return { bandeau: consentement.length > 0 || autres.length > 0, consentement, exemptes, sansTraceur, choisis, autres };
}

/* --------------------------------------------------------------------- */
/* Le contrôle                                                            */
/* --------------------------------------------------------------------- */

/**
 * Ce qui manque, ce qui est refusé, et ce qui mérite un mot sans rien bloquer.
 *
 * `manques` : un champ vide qui doit être rempli. `invalides` : un champ rempli
 * dont le contenu ne passe pas. Les confondre fait dire « il manque » devant
 * un champ visiblement écrit, voir hostingAuthorisation.js.
 */
export function valider(reponses) {
    const r = reponses || {};
    const manques = [];
    const invalides = [];
    const alertes = [];
    const requis = (cle) => {
        if (!nettoyer(r[cle])) manques.push(cle);
    };

    if (!["moi", "client"].includes(r.pourQui)) manques.push("pourQui");
    if (!["ei", "societe", "association"].includes(r.forme)) manques.push("forme");

    if (r.forme === "ei") {
        requis("prenoms");
        requis("nom");
        if (!["commerciale", "artisanale", "liberale"].includes(r.activite)) manques.push("activite");
        if (r.activite === "commerciale") requis("rcsVille");
    }
    if (r.forme === "societe") {
        requis("denomination");
        if (!FORMES_SOCIALES.includes(r.formeSociale)) manques.push("formeSociale");
        requis("capital");
        requis("rcsVille");
        requis("representant");
        const capital = nettoyer(r.capital).replace(/\s/g, "").replace(",", ".");
        if (capital && !(Number(capital) > 0)) invalides.push({ champ: "capital", motif: "format" });
    }
    if (r.forme === "association") {
        requis("nomAssociation");
        requis("president");
        const rna = nettoyer(r.rna).toUpperCase();
        if (!rna) manques.push("rna");
        else if (!/^W\d{9}$/.test(rna)) invalides.push({ champ: "rna", motif: "format" });
    }

    // Le SIREN est demandé à toute entreprise ; une association n'en a pas
    // toujours, et ne le donne que si elle en a un.
    if (r.forme === "ei" || r.forme === "societe") {
        const brut = CHIFFRES(r.siren);
        if (!brut) manques.push("siren");
        else if (!siren(brut)) invalides.push({ champ: "siren", motif: "format" });
        // La clé se vérifie sur les neuf chiffres du SIREN : un SIRET a la sienne,
        // calculée sur quatorze, et le lecteur peut taper l'un ou l'autre.
        else if (!luhn(siren(brut))) invalides.push({ champ: "siren", motif: "cle" });
    } else if (CHIFFRES(r.siren) && !siren(r.siren)) {
        invalides.push({ champ: "siren", motif: "format" });
    } else if (siren(r.siren) && !luhn(siren(r.siren))) {
        invalides.push({ champ: "siren", motif: "cle" });
    }

    requis("voie");
    const cp = nettoyer(r.codePostal);
    if (!cp) manques.push("codePostal");
    else if (!/^\d{5}$/.test(cp)) invalides.push({ champ: "codePostal", motif: "format" });
    requis("commune");

    if (!nettoyer(r.email)) manques.push("email");
    else if (!emailValide(r.email)) invalides.push({ champ: "email", motif: "format" });
    requis("telephone");

    if (r.tva === "assujetti") {
        if (!nettoyer(r.numeroTva)) manques.push("numeroTva");
        else if (!tvaFrancaiseValide(r.numeroTva)) invalides.push({ champ: "numeroTva", motif: "format" });
        else if (siren(r.siren) && CHIFFRES(r.numeroTva).toUpperCase().slice(4) !== siren(r.siren)) {
            alertes.push("tvaAutreSiren");
        }
    } else if (r.tva !== "franchise") {
        manques.push("tva");
    }

    // Deux régimes que Service Public distingue : la profession réglementée
    // (titre, règles, État membre, ordre) et l'activité soumise à une
    // autorisation (le nom et l'adresse de l'autorité qui l'a délivrée).
    const reglementee = reglementeeDe(r);
    if (reglementee === "profession") {
        requis("profession");
        requis("regles");
        requis("ordre");
    } else if (reglementee === "autorisation") {
        requis("autorite");
        requis("adresseAutorite");
    } else if (reglementee !== "non") {
        manques.push("reglementee");
    }

    requis("nomSite");
    if (!nettoyer(r.url)) manques.push("url");
    else if (!urlValide(r.url)) invalides.push({ champ: "url", motif: "format" });

    const hebergeurs = hebergeursComplets(r);
    if (!hebergeurs.length) manques.push("hebergeurs");
    hebergeurs.forEach((h, rang) => {
        if (!h.raisonSociale) manques.push(`hebergeur${rang}RaisonSociale`);
        if (!h.adresse) manques.push(`hebergeur${rang}Adresse`);
        if (!h.telephone) manques.push(`hebergeur${rang}Telephone`);
        if (!h.catalogue && h.horsUe && !h.pays) manques.push(`hebergeur${rang}Pays`);
    });
    if (hebergeurs.some((h) => h.telephoneInconnu)) alertes.push("telephoneHebergeurInconnu");
    // Deux fois le même rôle chez deux hébergeurs : « tout le site » chez l'un
    // et « l'interface » chez l'autre se contredisent.
    const roles = hebergeurs.map((h) => h.role);
    if (roles.includes("site") && roles.length > 1) alertes.push("roleSiteEtAutres");
    if (new Set(roles).size !== roles.length) alertes.push("roleEnDouble");

    if (r.emailDonnees && !emailValide(r.emailDonnees)) invalides.push({ champ: "emailDonnees", motif: "format" });

    if (r.pourQui === "client") {
        requis("prestataireNom");
        requis("signataireClient");
        if (!["madame", "monsieur"].includes(r.signataireCivilite)) manques.push("signataireCivilite");
    }
    autresOutils(r).forEach((o, rang) => {
        if (!o.nom) manques.push(`autreOutil${rang}Nom`);
        if (!o.societe) manques.push(`autreOutil${rang}Societe`);
        if (o.horsUe && !o.pays) manques.push(`autreOutil${rang}Pays`);
    });

    // Un outil de paiement sans commandes, ou une newsletter sans outil
    // d'envoi : rien de faux, mais la politique le dirait mal.
    const collectes = r.collectes || {};
    const outils = r.outils || {};
    const outilsDe = (categorie) =>
        Object.keys(outils).filter((id) => outils[id] && OUTILS_TIERS[id]?.categorie === categorie);
    if (outilsDe("paiement").length && !collectes.commandes) alertes.push("paiementSansCommandes");
    if (outilsDe("rendezVous").length && !collectes.rendezVous) alertes.push("rendezVousSansCollecte");
    if (outilsDe("emailing").length && !collectes.newsletter) alertes.push("emailingSansNewsletter");
    if (outilsDe("echange").length && !collectes.discussion) alertes.push("discussionSansCollecte");

    return {
        valide: manques.length === 0 && invalides.length === 0,
        manques: [...new Set(manques)],
        invalides,
        alertes,
    };
}

/** « non », « profession » ou « autorisation » ; `true` d'une ancienne réponse vaut profession. */
export function reglementeeDe(r) {
    if (r?.reglementee === false || r?.reglementee === "non") return "non";
    if (r?.reglementee === true || r?.reglementee === "profession") return "profession";
    if (r?.reglementee === "autorisation") return "autorisation";
    return "";
}

/* --------------------------------------------------------------------- */
/* L'éditeur                                                              */
/* --------------------------------------------------------------------- */

/**
 * Le nom de l'éditeur, tel qu'il doit apparaître.
 *
 * Un entrepreneur individuel porte « EI » à côté de son nom sur tout ce qu'il
 * publie pour son activité : c'est ce qui distingue son patrimoine
 * professionnel de son patrimoine personnel. Le nom commercial, s'il en a un,
 * vient après, parce qu'il ne remplace pas le nom.
 */
export function nomEditeur(r) {
    if (r.forme === "ei") return `${identite({ prenoms: r.prenoms, nom: r.nom })} EI`;
    if (r.forme === "societe") return nettoyer(r.denomination);
    return nettoyer(r.nomAssociation);
}

/** Le capital écrit en phrase : « 1 000 euros ». */
function capitalEnPhrase(valeur) {
    const n = Number(nettoyer(valeur).replace(/\s/g, "").replace(",", "."));
    if (!(n > 0)) return "";
    const centimes = !Number.isInteger(n);
    const chiffres = n.toLocaleString("fr-FR", { minimumFractionDigits: centimes ? 2 : 0, maximumFractionDigits: 2 }).replace(/ /g, " ");
    return `${chiffres} euros`;
}

function lignesEditeur(r) {
    const adresse = lignesDAdresse(r);
    const lignes = [];
    if (r.forme === "ei") {
        lignes.push(nomEditeur(r));
        if (nettoyer(r.nomCommercial)) lignes.push(`Nom commercial : ${nettoyer(r.nomCommercial)}`);
        lignes.push(...adresse);
        // La loi demande le numéro d'inscription à qui est inscrit au RCS, ou
        // au registre national des entreprises au titre de l'artisanat. Une
        // activité libérale n'a ni l'un ni l'autre : son SIREN suffit.
        if (r.activite === "commerciale") {
            lignes.push(`Immatriculation au RCS de ${nettoyer(r.rcsVille)} : ${sirenLisible(r.siren)}`);
        } else if (r.activite === "artisanale") {
            lignes.push(`Immatriculation au Registre national des entreprises : ${sirenLisible(r.siren)}`);
        } else {
            lignes.push(`SIREN : ${sirenLisible(r.siren)}`);
        }
    } else if (r.forme === "societe") {
        lignes.push(`${nettoyer(r.denomination)}, ${r.formeSociale} au capital de ${capitalEnPhrase(r.capital)}`);
        lignes.push(`Siège social : ${adressePostale(r)}`);
        lignes.push(`RCS ${nettoyer(r.rcsVille)} ${sirenLisible(r.siren)}`);
    } else {
        lignes.push(`${nettoyer(r.nomAssociation)}, association déclarée`);
        lignes.push(`Siège : ${adressePostale(r)}`);
        lignes.push(`Numéro RNA : ${nettoyer(r.rna).toUpperCase()}`);
        if (siren(r.siren)) lignes.push(`SIREN : ${sirenLisible(r.siren)}`);
    }
    lignes.push(`Téléphone : ${nettoyer(r.telephone)}`);
    lignes.push(`Adresse électronique : ${nettoyer(r.email)}`);
    if (r.tva === "assujetti") {
        lignes.push(`Numéro de TVA intracommunautaire : ${CHIFFRES(r.numeroTva).toUpperCase()}`);
    }
    return lignes;
}

/** Qui répond du contenu publié : le directeur de la publication. */
export function directeurPublication(r) {
    if (r.forme === "ei") return identite({ prenoms: r.prenoms, nom: r.nom });
    if (r.forme === "societe") return nettoyer(r.representant);
    return nettoyer(r.president);
}

/* --------------------------------------------------------------------- */
/* Les mentions légales                                                   */
/* --------------------------------------------------------------------- */

/** « L'interface du site est hébergée par : », selon ce que l'hébergeur porte. */
function phraseHebergement(role, seul) {
    if (seul || role === "site") return "Le site est hébergé par :";
    return ROLES_HEBERGEMENT[role]?.phrase || "Le site est hébergé par :";
}

export function composerMentions(reponses, aujourdhui) {
    const r = reponses || {};
    if (!valider(r).valide) return null;
    const hebergeurs = hebergeursComplets(r);
    const blocs = [
        {
            type: "paragraphe",
            texte: `Le site ${nettoyer(r.nomSite)}, accessible à l'adresse ${urlLisible(r.url)}, est édité par :`,
        },
        { type: "lignes", lignes: lignesEditeur(r) },
    ];

    if (reglementeeDe(r) === "profession") {
        blocs.push({ type: "intertitre", texte: "Profession réglementée" });
        blocs.push({
            type: "lignes",
            lignes: [
                `Titre professionnel : ${nettoyer(r.profession)}, obtenu en ${nettoyer(r.paysTitre) || "France"}`,
                `Ordre ou organisme professionnel : ${nettoyer(r.ordre)}`,
                `Règles professionnelles applicables : ${nettoyer(r.regles)}`,
            ],
        });
    } else if (reglementeeDe(r) === "autorisation") {
        blocs.push({ type: "intertitre", texte: "Activité soumise à autorisation" });
        blocs.push({
            type: "lignes",
            lignes: [
                `Autorisation délivrée par : ${nettoyer(r.autorite)}`,
                `Adresse de l'autorité : ${nettoyer(r.adresseAutorite)}`,
            ],
        });
    }

    blocs.push({ type: "intertitre", texte: "Directeur de la publication" });
    blocs.push({ type: "paragraphe", texte: directeurPublication(r) });

    blocs.push({ type: "intertitre", texte: hebergeurs.length > 1 ? "Hébergeurs" : "Hébergeur" });
    for (const h of hebergeurs) {
        blocs.push({ type: "paragraphe", texte: phraseHebergement(h.role, hebergeurs.length === 1) });
        blocs.push({ type: "lignes", lignes: [h.raisonSociale, h.adresse, `Téléphone : ${h.telephone}`] });
    }

    if (r.pourQui === "client" && r.creditCreateur !== false && nettoyer(r.prestataireNom)) {
        blocs.push({ type: "intertitre", texte: "Conception et réalisation" });
        blocs.push({
            type: "paragraphe",
            texte: `Site conçu et réalisé par ${nettoyer(r.prestataireNom)}${nettoyer(r.prestataireUrl) ? ` (${urlLisible(r.prestataireUrl)})` : ""}.`,
        });
    }

    blocs.push({ type: "intertitre", texte: "Données personnelles" });
    blocs.push({
        type: "paragraphe",
        texte:
            "La façon dont vos données personnelles sont collectées et traitées sur ce site, et la manière d'exercer vos droits, sont décrites dans la politique de confidentialité.",
    });
    blocs.push({
        type: "note",
        texte: `Mentions mises à jour le ${dateEnToutesLettres(String(aujourdhui).slice(0, 10))}.`,
    });

    return { titre: "Mentions légales", blocs };
}

/* --------------------------------------------------------------------- */
/* La politique de confidentialité                                        */
/* --------------------------------------------------------------------- */

/** Une liste lisible : « A », « A et B », « A, B et C ». */
function enumerer(mots) {
    if (mots.length <= 1) return mots.join("");
    return `${mots.slice(0, -1).join(", ")} et ${mots[mots.length - 1]}`;
}

/** Les outils choisis, d'une catégorie donnée, par leur nom. */
function outilsChoisis(r, filtre = () => true) {
    return Object.keys(r.outils || {})
        .filter((id) => r.outils[id] && OUTILS_TIERS[id] && filtre(OUTILS_TIERS[id]))
        .map((id) => ({ id, ...OUTILS_TIERS[id] }));
}

/**
 * Qui reçoit les données, en deux familles que la politique ne doit pas
 * confondre.
 *
 * LES PRESTATAIRES travaillent pour le compte de l'éditeur : l'hébergeur, le
 * service d'envoi de la lettre, l'outil de paiement. LES SERVICES QUI AGISSENT
 * AUSSI POUR LEUR PROPRE COMPTE réutilisent ce qu'ils reçoivent : Google pour
 * Analytics, ses vidéos et reCAPTCHA, Meta pour son pixel. Les ranger parmi les
 * premiers ferait dire à l'éditeur qu'ils lui obéissent, ce qui est faux.
 *
 * Une police chargée depuis un serveur tiers reçoit l'adresse IP du visiteur,
 * comme une vidéo intégrée : ils sont nommés, parce que c'est précisément ce
 * qu'on reproche le plus souvent à une politique recopiée d'ailleurs.
 */
export function destinataires(r) {
    const liste = hebergeursComplets(r).map((h) => ({
        nom: h.raisonSociale,
        role: ROLES_HEBERGEMENT[h.role]?.destinataire || "hébergement du site",
        horsUe: h.horsUe,
        pays: h.pays,
        propre: false,
    }));
    // Un outil installé sur le serveur du site ne transmet rien à personne
    // d'autre que l'hébergeur, déjà nommé.
    for (const outil of outilsChoisis(r).filter((o) => o.societe)) {
        liste.push({ nom: outil.societe, role: outil.usage, horsUe: outil.horsUe, pays: outil.pays, propre: Boolean(outil.propreCompte) });
        for (const autre of outil.aussi || []) {
            liste.push({ nom: autre.societe, role: autre.usage, horsUe: autre.horsUe, pays: autre.pays, propre: true });
        }
    }
    for (const o of autresOutils(r)) {
        liste.push({ nom: o.societe || o.nom, role: o.usage || o.nom, horsUe: o.horsUe, pays: o.pays, propre: false });
    }
    // Un même prestataire peut héberger le site et fournir un outil : il n'est
    // nommé qu'une fois, avec tous ses rôles.
    const parNom = new Map();
    for (const d of liste) {
        const deja = parNom.get(d.nom);
        if (deja) {
            if (!deja.roles.includes(d.role)) deja.roles.push(d.role);
            deja.horsUe = deja.horsUe || d.horsUe;
            deja.propre = deja.propre || d.propre;
        } else {
            parNom.set(d.nom, { nom: d.nom, roles: [d.role], horsUe: d.horsUe, pays: d.pays, propre: d.propre });
        }
    }
    return [...parNom.values()];
}

/** Ce que le site collecte, déclaré ou deviné de ses outils. */
function collecteQuelqueChose(r) {
    if (COLLECTES.some((c) => r.collectes?.[c])) return true;
    // Un agenda, une fenêtre de discussion, un envoi de lettre ou un paiement
    // reçoivent ce que le visiteur y tape : « aucune donnée » serait faux.
    return outilsChoisis(r, (o) => ["rendezVous", "echange", "emailing", "paiement"].includes(o.categorie)).length > 0 || autresOutils(r).length > 0;
}

/** Ce qu'un traceur fait, dit selon le verdict qui le range. */
function finaliteDe(id, exempte) {
    const o = OUTILS_TIERS[id];
    if (exempte) return o.finaliteExemptee || o.finaliteTraceur;
    return o.finaliteSansReglage || o.finaliteTraceur;
}

export function composerConfidentialite(reponses, aujourdhui) {
    const r = reponses || {};
    if (!valider(r).valide) return null;
    const editeur = nomEditeur(r);
    const contact = nettoyer(r.emailDonnees) || nettoyer(r.email);
    const collectes = COLLECTES.filter((c) => r.collectes?.[c]);
    const paiement = outilsChoisis(r, (o) => o.categorie === "paiement").length > 0;
    const blocs = [
        {
            type: "paragraphe",
            texte:
                `Cette politique explique quelles données personnelles le site ${nettoyer(r.nomSite)} ` +
                "collecte, pourquoi, combien de temps elles sont gardées, à qui elles sont transmises " +
                "et comment exercer vos droits.",
        },
        { type: "intertitre", texte: "Le responsable du traitement" },
        {
            type: "paragraphe",
            texte:
                `Vos données sont traitées par ${editeur}, ${adressePostale(r)}. ` +
                `Pour toute question sur vos données, écrivez à ${contact}.`,
        },
    ];

    if (!collecteQuelqueChose(r)) {
        blocs.push({ type: "intertitre", texte: "Les données collectées" });
        blocs.push({
            type: "paragraphe",
            texte:
                "Ce site ne vous demande aucune donnée personnelle : il ne comporte ni formulaire, ni " +
                "inscription, ni espace client. Les seules données traitées sont celles que votre " +
                "navigateur transmet pour afficher les pages, décrites plus bas.",
        });
    }

    for (const cle of collectes) {
        const t = TRAITEMENTS[cle];
        const outils = outilsChoisis(r, (o) => o.categorie === t.categorieOutil).map((o) => o.nom);
        blocs.push({ type: "intertitre", texte: t.titre });
        blocs.push({
            type: "liste",
            points: [
                `Les données : ${t.donnees}.`,
                `Ce qui est obligatoire : ${t.obligatoire}.`,
                `Pourquoi : ${t.finalite}${outils.length ? `, avec ${enumerer(outils)}` : ""}.`,
                `Sur quelle base : ${t.base}.`,
                `Combien de temps : ${t.duree}.`,
            ],
        });
        // Le paragraphe sur la carte bancaire suppose un prestataire de
        // paiement : sans lui, il parlerait d'un tiers qui n'existe pas.
        if (t.complement && (!t.complementSiPaiement || paiement)) blocs.push({ type: "paragraphe", texte: t.complement });
    }

    blocs.push({ type: "intertitre", texte: "Les données de navigation" });
    blocs.push({
        type: "paragraphe",
        texte:
            "Comme tout site, celui-ci reçoit des informations techniques à chaque visite (adresse IP, " +
            "type de navigateur, pages demandées). Elles servent à afficher les pages et à protéger le " +
            "site contre les abus. Elles sont traitées sur la base de notre intérêt légitime à assurer " +
            "le fonctionnement et la sécurité du site, et gardées dans les journaux techniques de " +
            "l'hébergeur le temps nécessaire à ces deux usages.",
    });

    const recus = destinataires(r);
    const prestataires = recus.filter((d) => !d.propre);
    const proprio = recus.filter((d) => d.propre);
    blocs.push({ type: "intertitre", texte: "Qui reçoit vos données" });
    blocs.push({
        type: "paragraphe",
        texte:
            "Vos données ne sont ni vendues ni louées. Elles sont accessibles à l'éditeur du site et aux " +
            "prestataires qui les traitent pour son compte, chacun pour ce qui le concerne :",
    });
    blocs.push({ type: "liste", points: prestataires.map((d) => `${d.nom} : ${enumerer(d.roles)}.`) });
    if (proprio.length) {
        blocs.push({
            type: "paragraphe",
            texte:
                "Les services suivants reçoivent aussi des données quand vous les utilisez, et les " +
                "réutilisent pour leur propre compte, selon leur politique de confidentialité :",
        });
        blocs.push({ type: "liste", points: proprio.map((d) => `${d.nom} : ${enumerer(d.roles)}.`) });
    }

    const horsUe = recus.filter((d) => d.horsUe);
    if (horsUe.length) {
        blocs.push({ type: "intertitre", texte: "Les transferts hors de l'Union européenne" });
        blocs.push({
            type: "paragraphe",
            texte:
                `${horsUe.length > 1 ? "Ces destinataires sont établis" : "Ce destinataire est établi"} hors de ` +
                `l'Union européenne, ou ${horsUe.length > 1 ? "peuvent y traiter" : "peut y traiter"} vos données :`,
        });
        blocs.push({ type: "liste", points: horsUe.map((d) => (d.pays ? `${d.nom}, ${d.pays}.` : `${d.nom}.`)) });
        blocs.push({
            type: "paragraphe",
            texte:
                "Le règlement européen n'autorise ces transferts qu'avec des garanties : une décision " +
                "d'adéquation de la Commission européenne quand le pays ou le destinataire en bénéficie " +
                "(pour les États-Unis, les entreprises certifiées au Data Privacy Framework, décision du " +
                "10 juillet 2023), ou à défaut des garanties prévues par contrat, comme les clauses " +
                "contractuelles types adoptées par la Commission. Celles qui s'appliquent à chaque " +
                "destinataire figurent dans son contrat et dans sa politique de confidentialité.",
        });
    }

    const cookies = verdictCookies(r);
    blocs.push({ type: "intertitre", texte: "Les cookies et autres traceurs" });
    if (cookies.bandeau) {
        blocs.push({
            type: "paragraphe",
            texte:
                "Certains traceurs ne sont déposés qu'avec votre accord, que vous donnez ou refusez dans " +
                "le bandeau affiché à votre première visite. Vous pouvez changer d'avis à tout moment " +
                "depuis le lien de gestion des cookies, en bas de chaque page. Refuser ne vous empêche pas " +
                "d'utiliser le site. Les traceurs soumis à votre accord :",
        });
        blocs.push({
            type: "liste",
            points: [
                ...cookies.consentement.map((id) => `${OUTILS_TIERS[id].nom} : ${finaliteDe(id, false)}.`),
                ...autresOutils(r)
                    .filter((o) => o.traceurs)
                    .map((o) => `${o.nom || o.societe}${o.usage ? ` : ${o.usage}` : ""}.`),
            ],
        });
    }
    if (cookies.exemptes.length) {
        blocs.push({
            type: "paragraphe",
            texte: cookies.bandeau
                ? "D'autres traceurs ne demandent pas votre accord, parce qu'ils sont nécessaires au service que vous demandez ou limités à une mesure d'audience anonyme :"
                : "Ce site ne dépose que des traceurs qui ne demandent pas votre accord, parce qu'ils sont nécessaires au service que vous demandez ou limités à une mesure d'audience anonyme :",
        });
        blocs.push({
            type: "liste",
            points: cookies.exemptes.map((id) => `${OUTILS_TIERS[id].nom} : ${finaliteDe(id, true)}.`),
        });
    }
    if (!cookies.bandeau && !cookies.exemptes.length) {
        blocs.push({
            type: "paragraphe",
            texte: "Ce site ne dépose aucun cookie ni autre traceur soumis à votre accord.",
        });
    }

    blocs.push({ type: "intertitre", texte: "Vos droits" });
    blocs.push({
        type: "paragraphe",
        texte: "Sur les données qui vous concernent, et selon la base sur laquelle chaque traitement repose, vous pouvez :",
    });
    blocs.push({
        type: "liste",
        points: [
            "y accéder et en obtenir une copie ;",
            "les faire rectifier ou compléter ;",
            "en demander l'effacement ;",
            "en demander la limitation, ou vous opposer à un traitement fondé sur notre intérêt légitime ;",
            "recevoir celles que vous nous avez fournies dans un format lisible par une machine, quand le traitement repose sur votre consentement ou sur un contrat ;",
            "retirer votre consentement à tout moment, quand le traitement repose sur lui, sans que cela remette en cause ce qui a été fait avant ;",
            "définir des directives sur le sort de vos données après votre décès.",
        ],
    });
    blocs.push({
        type: "paragraphe",
        texte:
            `Pour exercer ces droits, écrivez à ${contact}. Une réponse vous est apportée dans un délai ` +
            "d'un mois, qui peut être prolongé de deux mois si la demande est complexe ; vous en êtes " +
            "alors prévenu. Si vous estimez, après nous avoir contactés, que vos droits ne sont pas " +
            "respectés, vous pouvez adresser une réclamation à la CNIL, sur www.cnil.fr.",
    });
    blocs.push({
        type: "note",
        texte: `Politique mise à jour le ${dateEnToutesLettres(String(aujourdhui).slice(0, 10))}.`,
    });

    return { titre: "Politique de confidentialité", blocs };
}

/* --------------------------------------------------------------------- */
/* La fiche de validation du client                                       */
/* --------------------------------------------------------------------- */

/**
 * Ce que le client signe quand un prestataire a préparé ses documents.
 *
 * Elle ne décharge le prestataire de rien qu'il aurait mal fait : elle
 * constate que les informations viennent du client et qu'il a relu le texte.
 *
 * LA DATE RESTE EN BLANC. La fiche se prépare le jour où le site est livré,
 * et le client la signe souvent plus tard, à la main : une date imprimée
 * d'avance dirait qu'il a validé un jour où il n'avait encore rien lu.
 */
export function composerValidation(reponses) {
    const r = reponses || {};
    if (r.pourQui !== "client" || !valider(r).valide) return null;
    const lieu = nettoyer(r.lieuDeSignature);
    const accord = r.signataireCivilite === "madame" ? "e" : "";
    return {
        titre: `Validation des mentions légales et de la politique de confidentialité du site ${nettoyer(r.nomSite)}`,
        blocs: [
            {
                type: "paragraphe",
                texte:
                    `Je soussigné${accord} ${nettoyer(r.signataireClient)}, agissant pour ${nomEditeur(r)}, ` +
                    `éditeur du site ${urlLisible(r.url)}, déclare :`,
            },
            {
                type: "liste",
                points: [
                    "avoir fourni moi-même les informations qui figurent dans les mentions légales et dans la politique de confidentialité du site ;",
                    "avoir relu ces deux documents dans leur version jointe, et en valider le contenu ;",
                    "avoir déclaré tous les outils tiers chargés par le site à la date de la présente validation ;",
                    `savoir que ${nettoyer(r.prestataireNom)} les a intégrés au site à partir de mes réponses, sans me délivrer de conseil juridique, et que leur publication fait partie de mes obligations d'éditeur du site ;`,
                    `m'engager à prévenir ${nettoyer(r.prestataireNom)} de tout changement qui demanderait de les mettre à jour : adresse, hébergeur, outil ajouté au site, nouvelle collecte de données.`,
                ],
            },
            {
                type: "paragraphe",
                texte: `${lieu ? `Fait à ${lieu}, le` : "Le"} ____________________`,
            },
            { type: "signature", nom: nettoyer(r.signataireClient) },
        ],
    };
}
